// src/routes/pre-campionatura/index.tsx
/**
 * Pagina "Pre-Campionatura": coda di lavoro per il team Ufficio Stile che gestisce la
 * fase preliminare delle richieste di Campionatura, prima che passino al team
 * Industrializzazione per la lavorazione vera e propria (pagina /campioni).
 * Stessa richiesta, stesso tipo (Campionatura): cambia solo il filtro di stato di default.
 */
import React, { useState } from 'react';
import {
  Container,
  Title,
  Paper,
  Group,
  Button,
  Stack,
  Text,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { RequestModal } from '../requests/components/RequestModal';
import { RequestsTableServerPaginated } from '../requests/components/RequestsTableServerPaginated';
import { useRequestTypePageState } from '../../hooks/use-request-type-page-state';
import {
  RequestTypeFilters,
  RequestStatusCountsBadges,
} from '../../components/requests/filters';
import { Request as RequestType, RequestType as RequestTypeEnum, RequestStatus } from '../../types/api';
import { getStatusesForType } from '../../utils/request-status-utils';

const PRE_CAMPIONATURA_STATUSES = [RequestStatus.PreCampionatura, RequestStatus.PreCampionaturaCompleted];

export function PreCampionaturaPage() {
  const navigate = useNavigate();
  const [createModalOpened, setCreateModalOpened] = useState(false);

  const state = useRequestTypePageState({
    type: RequestTypeEnum.Campionatura,
    route: '/pre-campionatura',
    defaultStatuses: PRE_CAMPIONATURA_STATUSES,
  });

  const handleViewRequest = (id: number | string) => {
    navigate({ to: `/requests/${id}`, search: { from: '/pre-campionatura' } });
  };

  const handleEditRequest = (id: number | string) => {
    navigate({ to: `/requests/${id}/edit`, search: { from: '/pre-campionatura' } });
  };

  const handleRequestCreated = (_request: RequestType) => {
    setCreateModalOpened(false);
    state.invalidate();
  };

  return (
    <Container size="xl" py="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Title order={2}>Pre-Campionatura</Title>
            <Text size="sm" c="dimmed">
              Richieste di Campionatura in attesa di completare la fase preliminare, prima del
              passaggio al team Industrializzazione.
            </Text>
          </div>
          <Button
            color="blue"
            leftSection={<IconPlus size={16} />}
            onClick={() => setCreateModalOpened(true)}
            styles={{ label: { color: 'white' }, section: { color: 'white' } }}
          >
            Nuova Richiesta Campionatura
          </Button>
        </Group>

        <Paper withBorder p="md">
          <Stack gap="md">
            <RequestStatusCountsBadges
              type={RequestTypeEnum.Campionatura}
              aggregates={state.aggregates}
              loading={state.aggregatesLoading}
              activeStatus={state.activeStatus}
              onStatusToggle={state.handleStatusToggle}
              visibleStatuses={PRE_CAMPIONATURA_STATUSES}
            />

            <RequestTypeFilters
              value={state.filters}
              onChange={state.handleFiltersChange}
              onReset={state.handleResetFilters}
              availableYears={state.availableYears}
              availableStatuses={getStatusesForType(RequestTypeEnum.Campionatura)}
            />

            <RequestsTableServerPaginated
              data={state.items}
              onView={handleViewRequest}
              onEdit={handleEditRequest}
              loading={state.isLoading}
              fetching={state.isFetching}
              totalCount={state.totalCount}
              totalPages={state.totalPages}
              pageNumber={state.pageNumber}
              pageSize={state.pageSize}
              onPageChange={state.handlePageChange}
              onPageSizeChange={state.handlePageSizeChange}
            />
          </Stack>
        </Paper>
      </Stack>

      <RequestModal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        mode="create"
        onSuccess={handleRequestCreated}
        defaultRequestType={String(RequestTypeEnum.Campionatura)}
      />
    </Container>
  );
}
